<!--
  BaseModal — базовая большая модалка для форм и сложных экранов:
  настройки таблицы, редактирование сущности, окна с сайдбаром и своим footer

  BaseDialog оставляем для маленьких подтверждений вместо alert/confirm
-->
<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="base-modal-overlay"
      :class="{
        'base-modal-overlay--adaptive-fullscreen': adaptiveFullscreen,
      }"
      @mousedown.self="handleOverlayClick"
    >
      <section
        v-bind="$attrs"
        class="card base-modal"
        :class="[
          `base-modal--${size}`,
          {
            'base-modal--with-footer': $slots.footer,
            'base-modal--adaptive-fullscreen': adaptiveFullscreen,
          },
        ]"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : null"
        tabindex="-1"
        ref="modalRef"
      >
        <header class="base-modal__header">
          <slot name="header">
            <h3 v-if="title" class="base-modal__title" :id="titleId">
              {{ title }}
            </h3>
          </slot>

          <button
            v-if="showClose"
            class="base-modal__close"
            type="button"
            aria-label="Закрыть"
            @click="requestClose"
          >
            <AppIcon name="close" :size="20" />
          </button>
        </header>

        <div
          class="base-modal__body"
          :class="`base-modal__body--padding-${bodyPadding}`"
        >
          <slot />
        </div>

        <footer
          v-if="$slots.footer"
          class="base-modal__footer"
          :class="[
            `base-modal__footer--align-${footerAlign}`,
            { 'base-modal__footer--elevated': elevatedFooter },
          ]"
        >
          <slot name="footer" />
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import { createModalTabHandler, restoreModalFocus } from "@/utils/modalFocusUtils";

let lockedModalCount = 0;
let nextModalId = 1;
const activeModalStack = [];

export default {
  name: "BaseModal",
  inheritAttrs: false,

  components: {
    AppIcon,
  },

  props: {
    // Управляет отображением модалки снаружи, BaseModal сам состояние не хранит
    visible: {
      type: Boolean,
      default: false,
    },
    // Стандартный заголовок; для нестандартной шапки используй header slot
    title: {
      type: String,
      default: "",
    },
    // Семантический размер оболочки под типовой сценарий модалки
    size: {
      type: String,
      default: "lg",
      validator: (value) =>
        ["md", "lg", "xl", "wide", "fullscreen"].includes(value),
    },
    // Позволяет большим рабочим областям управлять своими отступами внутри body
    bodyPadding: {
      type: String,
      default: "default",
      validator: (value) => ["default", "none"].includes(value),
    },
    // Скрывает крестик, если закрытие должно быть доступно только через footer
    showClose: {
      type: Boolean,
      default: true,
    },
    // Выравнивает стандартный footer без доменных CSS-классов
    footerAlign: {
      type: String,
      default: "end",
      validator: (value) =>
        ["start", "end", "between"].includes(value),
    },
    // Добавляет визуальное отделение footer для прокручиваемого body
    elevatedFooter: {
      type: Boolean,
      default: false,
    },
    // Делает сложную модалку полноэкранной на средних и маленьких экранах
    adaptiveFullscreen: {
      type: Boolean,
      default: false,
    },
    // Разрешает закрытие кликом по overlay, если сценарий не блокирующий
    closeOnOverlay: {
      type: Boolean,
      default: true,
    },
    // Разрешает закрытие по Escape только для верхней модалки в стеке
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["close"],

  data() {
    return {
      titleId: `base-modal-title-${Math.random()
        .toString(36)
        .slice(2)}`,
      modalId: nextModalId++,
      isBodyLocked: false,
      handleKeydown: null,
      focusReturnTarget: null,
    };
  },

  watch: {
    visible(val) {
      if (val) {
        this.openModal();
      } else {
        this.closeModal();
      }
    },
  },

  methods: {
    handleOverlayClick() {
      if (this.closeOnOverlay) this.requestClose();
    },

    requestClose() {
      this.$emit("close");
    },

    openModal() {
      if (!activeModalStack.includes(this.modalId)) {
        this.focusReturnTarget = document.activeElement;
      }
      this.registerActiveModal();
      this.lockBodyScroll();
      this.$nextTick(() => this.trapFocus());
    },

    closeModal() {
      const modal = this.$refs.modalRef;
      const wasTopModal = this.isTopModal();
      const focusReturnTarget = this.focusReturnTarget;
      this.focusReturnTarget = null;
      this.unregisterActiveModal();
      this.unlockBodyScroll();
      this.removeKeydownListener();
      restoreModalFocus(focusReturnTarget, modal, wasTopModal);
    },

    registerActiveModal() {
      if (activeModalStack.includes(this.modalId)) return;

      activeModalStack.push(this.modalId);
    },

    unregisterActiveModal() {
      const index = activeModalStack.indexOf(this.modalId);
      if (index === -1) return;

      activeModalStack.splice(index, 1);
    },

    isTopModal() {
      return (
        activeModalStack[activeModalStack.length - 1] === this.modalId
      );
    },

    lockBodyScroll() {
      if (this.isBodyLocked) return;

      lockedModalCount += 1;
      this.isBodyLocked = true;
      document.body.style.overflow = "hidden";
    },

    unlockBodyScroll() {
      if (!this.isBodyLocked) return;

      lockedModalCount = Math.max(0, lockedModalCount - 1);
      this.isBodyLocked = false;

      if (lockedModalCount === 0) {
        document.body.style.overflow = "";
      }
    },

    removeKeydownListener() {
      if (!this.handleKeydown) return;

      document.removeEventListener("keydown", this.handleKeydown);
      this.handleKeydown = null;
    },

    trapFocus() {
      const modal = this.$refs.modalRef;
      if (!modal) return;

      // Одного перехвата Tab недостаточно: при открытии фокус ещё на странице под модалкой.
      if (this.isTopModal() && !modal.contains(document.activeElement)) {
        modal.focus({ preventScroll: true });
      }

      const handleTab = createModalTabHandler(modal);

      this.removeKeydownListener();

      this.handleKeydown = (event) => {
        if (!this.isTopModal()) return;

        if (event.key === "Escape") {
          if (this.closeOnEscape) this.requestClose();
          return;
        }

        handleTab(event);
      };

      document.addEventListener("keydown", this.handleKeydown);
    },
  },

  mounted() {
    if (this.visible) this.openModal();
  },

  beforeUnmount() {
    this.closeModal();
  },
};
</script>

<style scoped>
.base-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.base-modal {
  width: 100%;
  max-height: 90dvh;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  background: #fff;
  border-radius: 8px;
}

.base-modal:focus {
  outline: none;
}

.base-modal--md {
  max-width: 500px;
}

.base-modal--lg {
  max-width: 720px;
}

.base-modal--xl {
  max-width: 960px;
}

.base-modal--wide {
  max-width: 1100px;
}

.base-modal--fullscreen {
  width: calc(100dvw - 32px);
  max-width: none;
  height: calc(100dvh - 32px);
}

.base-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid var(--color-grey);
  flex-shrink: 0;
}

.base-modal__title {
  margin: 0;
  font-size: 18px;
  line-height: 1.3;
  word-break: break-word;
}

.base-modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -6px -8px -6px 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.base-modal__close:hover {
  background-color: #f3f4f6;
  color: var(--color-dark);
}

.base-modal__body {
  flex: 1;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
}

.base-modal__body--padding-none {
  padding: 0;
}

.base-modal__body::-webkit-scrollbar {
  width: 6px;
}

.base-modal__body::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.base-modal__body::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

.base-modal__footer {
  display: flex;
  gap: 15px;
  padding: 20px;
  border-top: 1px solid var(--color-grey);
  background-color: #fff;
  flex-shrink: 0;
}

.base-modal__footer--align-start {
  justify-content: flex-start;
}

.base-modal__footer--align-end {
  justify-content: flex-end;
}

.base-modal__footer--align-between {
  justify-content: space-between;
}

.base-modal__footer--elevated {
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

@media (max-width: 1400px) {
  .base-modal-overlay--adaptive-fullscreen {
    align-items: stretch;
    padding: 0;
  }

  .base-modal--adaptive-fullscreen {
    width: 100dvw;
    max-width: none;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
  }
}

@media (max-width: 767px) {
  .base-modal-overlay {
    align-items: stretch;
    padding: 0;
  }

  .base-modal,
  .base-modal--fullscreen {
    width: 100dvw;
    max-width: none;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
  }

  .base-modal__header {
    padding: 20px;
  }

  .base-modal__body {
    padding: 20px;
  }

  .base-modal__body--padding-none {
    padding: 0;
  }

  .base-modal__footer {
    flex-direction: column-reverse;
    padding: 14px 20px 20px;
  }

  .base-modal__footer :deep(button) {
    width: 100%;
  }
}
</style>
